import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { TextField, Button, Container, Typography, Box, InputAdornment } from '@mui/material';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import firebase from 'firebase/app';
import { getAuth, signInWithCredential, OAuthProvider } from 'firebase/auth';
import { Business } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';

// MSAL configuration
const msalConfig = {
    auth: {
        clientId: 'a21fac3f-8ff7-49c1-b650-7eaf0561c54b', // Replace with your Office 365 client ID
        authority: 'https://login.microsoft.com/1181d7b0-21cb-4658-82ff-083d36b8de45', // Replace with your tenant ID
        redirectUri: 'http://localhost:3000', // Replace with your redirect URI
    },
};

const msalInstance = new PublicClientApplication(msalConfig);

// Styled components
const StyledContainer = styled(Container)({
    padding: '40px',
    borderRadius: '10px',
    backgroundColor: '#fff', // Plain background for a sleek look
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', // Subtle shadow for depth
});

const StyledButton = styled(Button)({
    borderRadius: '20px',
    transition: 'background-color 0.3s ease',
    '&:hover': {
        backgroundColor: '#e0e0e0', // Subtle hover effect
    },
});

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { instance } = useMsal();

    const handleLogin = async (event) => {
        event.preventDefault();
        try {
            await login(email, password);
            console.log('Login successful, navigating to dashboard');
            setTimeout(() => navigate('/dashboard'), 1000);
        } catch (error) {
            setError('Login failed. Please check your credentials.');
            console.error('Login failed:', error);
        }
    };

    const handleOffice365Login = async () => {
        try {
            console.log('Starting Office 365 login process');
            const loginResponse = await instance.loginPopup({
                scopes: ['User.Read'],
            });

            if (!loginResponse.idToken || !loginResponse.accessToken) {
                throw new Error('ID Token or Access Token is missing from login response');
            }

            console.log('Login Response:', loginResponse);

            const auth = getAuth();
            const provider = new OAuthProvider('microsoft.com');
            const credential = provider.credential({
                idToken: loginResponse.idToken,
                accessToken: loginResponse.accessToken
            });

            console.log('Credential:', credential);

            await signInWithCredential(auth, credential);

            console.log('Office 365 login successful, navigating to dashboard');
            navigate('/dashboard');
        } catch (error) {
            setError('Office 365 login failed. Please try again.');
            console.error('Office 365 login failed:', error);
        }
    };

    return (
        <MsalProvider instance={msalInstance}>
            <StyledContainer component="main" maxWidth="xs">
                <Typography component="h1" variant="h4" align="center" sx={{ fontWeight: 'bold', color: '#333', mb: 3 }}>
                    Welcome Back
                </Typography>
                <Box component="form" noValidate sx={{ mt: 1 }} onSubmit={handleLogin}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment>,
                        }}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
                        }}
                    />
                    {error && (
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    )}
                    <StyledButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 3, mb: 2 }}
                    >
                        Sign In
                    </StyledButton>
                    <StyledButton
                        fullWidth
                        variant="contained"
                        color="secondary"
                        sx={{ mt: 1, mb: 2 }}
                        onClick={handleOffice365Login}
                        disabled
                    >
                        <Business sx={{ mr: 1 }} />
                        Sign in with Office 365
                    </StyledButton>
                    <Box textAlign="center">
                        <Link to="/passwordreset" variant="body2" sx={{ color: '#333' }}>
                            Forgot Password?
                        </Link>
                    </Box>
                </Box>
            </StyledContainer>
        </MsalProvider>
    );
};

export default Login;